// src/pages/Estatisticas/components/UsageEvolutionChart.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UsageEvolutionChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    carregarEvolucao();
  }, []);

  const carregarEvolucao = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/estatisticas/evolucao-uso');
      
      if (response.data.success) {
        // Ordenar por data crescente para o gráfico
        const sortedData = response.data.data.reverse();
        setData(sortedData);
      } else {
        setError(response.data.message || 'Erro ao carregar evolução');
      }
    } catch (err) {
      console.error('Error loading evolution:', err);
      setError('Erro ao carregar evolução: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataString) => {
    const date = new Date(dataString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const formatarNumero = (numero) => {
    if (numero >= 1000000) {
      return (numero / 1000000).toFixed(1) + 'M';
    } else if (numero >= 1000) {
      return (numero / 1000).toFixed(1) + 'K';
    }
    return numero.toString();
  };

  const renderChart = () => {
    if (data.length === 0) return null;

    const width = 800;
    const height = 300;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Calcular valores máximos e mínimos
    const maxTokens = Math.max(...data.map(d => d.totalTokens));
    const minTokens = 0;

    // Criar pontos do gráfico
    const points = data.map((d, i) => {
      const x = padding.left + (i / (data.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - ((d.totalTokens - minTokens) / (maxTokens - minTokens)) * chartHeight;
      return { x, y, date: d.date, tokens: d.totalTokens };
    });

    // Criar path do gráfico
    const pathData = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    // Criar área preenchida
    const areaPath = pathData + 
      ` L ${points[points.length - 1].x} ${padding.top + chartHeight}` +
      ` L ${padding.left} ${padding.top + chartHeight} Z`;

    // Calcular linhas de grade horizontais
    const gridLines = 5;
    const yGridPoints = Array.from({ length: gridLines }, (_, i) => {
      const value = maxTokens * (1 - i / (gridLines - 1));
      const y = padding.top + (i / (gridLines - 1)) * chartHeight;
      return { y, value };
    });

    return (
      <div className="chart-container">
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Linhas de grade horizontais */}
          {yGridPoints.map((point, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={point.y}
                x2={width - padding.right}
                y2={point.y}
                stroke="#e0e0e0"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
              <text
                x={padding.left - 10}
                y={point.y + 5}
                textAnchor="end"
                fontSize="12"
                fill="#666"
              >
                {formatarNumero(point.value)}
              </text>
            </g>
          ))}

          {/* Área preenchida do gráfico */}
          <path
            d={areaPath}
            fill="url(#gradient)"
            opacity="0.3"
          />

          {/* Linha do gráfico */}
          <path
            d={pathData}
            fill="none"
            stroke="#2196F3"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Pontos no gráfico */}
          {points.map((point, i) => (
            <g key={i}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill="#2196F3"
                stroke="white"
                strokeWidth="2"
              />
              {/* Mostrar label apenas para alguns pontos (evitar sobreposição) */}
              {i % Math.ceil(data.length / 10) === 0 && (
                <text
                  x={point.x}
                  y={padding.top + chartHeight + 25}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#666"
                >
                  {formatarData(point.date)}
                </text>
              )}
            </g>
          ))}

          {/* Gradiente para a área */}
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#2196F3" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#2196F3" stopOpacity="0.1" />
            </linearGradient>
          </defs>

          {/* Eixos */}
          <line
            x1={padding.left}
            y1={padding.top + chartHeight}
            x2={width - padding.right}
            y2={padding.top + chartHeight}
            stroke="#333"
            strokeWidth="2"
          />
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={padding.top + chartHeight}
            stroke="#333"
            strokeWidth="2"
          />
        </svg>

        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#2196F3' }}></span>
            Total de Tokens
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="stat-box">
        <h3>📈 Evolução de Uso (Últimos 60 Dias)</h3>
        <div className="loading-stats">
          <span>⏳</span>
          <span>Carregando gráfico...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stat-box">
        <h3>📈 Evolução de Uso (Últimos 60 Dias)</h3>
        <div className="error-stats">{error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="stat-box">
        <h3>📈 Evolução de Uso (Últimos 30 Dias)</h3>
        <div className="no-stats">
          <span>📊</span>
          <span>Nenhum dado disponível para o período</span>
        </div>
      </div>
    );
  }

  return (
    <div className="stat-box">
      <h3>📈 Evolução de Uso (Últimos 30 Dias)</h3>
      {renderChart()}
      <div className="chart-summary">
        <p>Período: {formatarData(data[0].date)} a {formatarData(data[data.length - 1].date)}</p>
        <p>Total de {data.length} dias com atividade</p>
      </div>
    </div>
  );
};

export default UsageEvolutionChart;